import { Component, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ControlValidators, DatepickerComponent, extractFormControlNames, FormControlFeedbackComponent, getMinDate, LoaderSpinnerComponent, ObjectPropertyNamesHolder, StoreLoader, UnsubscriberClass, ValidationService, ValidationServiceHelper } from '@aston/foundation';
import { Store } from '@ngrx/store';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { ContactFormStoreActions, ContactFormStoreSelectors } from 'apps/console/src/app/root-store/contact-form-store';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';

import { IContactForm } from '../../models';
import { newContactForm } from '../../functions';
import { IContactFormValidator } from '../../enums/validators/contact-form.validators';
import { ConsoleDomainValidators } from '../../enums/validators';
import { ContactFormRecallTime, ContactFormSubject } from '../../enums';

export const formControlNames = (name: keyof IContactForm) => name;

export type FormValidatorFieldsType = IContactFormValidator['fields'];

@Component({
	selector: 'contact-form',
	templateUrl: './contact-form.component.html',
	imports: [FormsModule, TranslateModule, ReactiveFormsModule, DatepickerComponent, LoaderSpinnerComponent, FormControlFeedbackComponent, AsyncPipe]
})
export class ContactFormComponent extends UnsubscriberClass implements OnInit {
	private store = inject(Store);
	private formBuilder = inject(UntypedFormBuilder);
	private validationService = inject(ValidationService);


	form: UntypedFormGroup;
	formControlNames: ObjectPropertyNamesHolder<IContactForm>;

	validationHelper: ValidationServiceHelper;
	rulesetFields: FormValidatorFieldsType;

	formIsOpened$ = this.store.select(ContactFormStoreSelectors.selectIsOpened).pipe(
		filter(isOpened => !isOpened),
		tap(_ => this.resetForm())
	);

	formLoader = new StoreLoader(
		this.store.select(ContactFormStoreSelectors.selectFormIsLoading),
		this.store.select(ContactFormStoreSelectors.selectFormError)
	);

	contactFormSubject = ContactFormSubject;
	subjects = Object.values(ContactFormSubject);

	contactFormRecallTime = ContactFormRecallTime;
	recallTimes = Object.values(ContactFormRecallTime);

	recallDateMin: Date = new Date();

	ngOnInit() {
		this.form = this.formBuilder.group({});
		this.form.addControl(formControlNames('lastName'), new UntypedFormControl());
		this.form.addControl(formControlNames('firstName'), new UntypedFormControl());
		this.form.addControl(formControlNames('email'), new UntypedFormControl());
		this.form.addControl(formControlNames('phoneNumber'), new UntypedFormControl());
		this.form.addControl(formControlNames('subject'), new UntypedFormControl());
		this.form.addControl(formControlNames('body'), new UntypedFormControl());
		this.form.addControl(formControlNames('canRecall'), new UntypedFormControl());
		this.form.addControl(formControlNames('recallDate'), new UntypedFormControl());
		this.form.addControl(formControlNames('recallTime'), new UntypedFormControl());

		this.formControlNames = extractFormControlNames<IContactForm>(this.form);

		this.validationService.getHelper(ConsoleDomainValidators.ContactFormValidator)
			.pipe(tap(([helper, fields]) => {
				this.validationHelper = helper;
				this.rulesetFields = fields as FormValidatorFieldsType;
				this.setFormValidators();
			}))
			.pipe(takeUntil(this.destroySubscriptions$))
			.subscribe();

		this.formIsOpened$
			.pipe(takeUntil(this.destroySubscriptions$))
			.subscribe();
	}

	get lastnameField(): UntypedFormControl {
		return this.form.get(formControlNames('lastName')) as UntypedFormControl;
	}

	get firstnameField(): UntypedFormControl {
		return this.form.get(formControlNames('firstName')) as UntypedFormControl;
	}

	get emailField(): UntypedFormControl {
		return this.form.get(formControlNames('email')) as UntypedFormControl;
	}

	get phoneNumberField(): UntypedFormControl {
		return this.form.get(formControlNames('phoneNumber')) as UntypedFormControl;
	}

	get subjectField(): UntypedFormControl {
		return this.form.get(formControlNames('subject')) as UntypedFormControl;
	}

	get bodyField(): UntypedFormControl {
		return this.form.get(formControlNames('body')) as UntypedFormControl;
	}

	get canRecallField(): UntypedFormControl {
		return this.form.get(formControlNames('canRecall')) as UntypedFormControl;
	}

	get isCanRecallChecked(): boolean {
		return this.canRecallField.value as boolean;
	}

	get recallDateField(): UntypedFormControl {
		return this.form.get(formControlNames('recallDate')) as UntypedFormControl;
	}

	get recallTimeField(): UntypedFormControl {
		return this.form.get(formControlNames('recallTime')) as UntypedFormControl;
	}

	onToggle() {
		this.store.dispatch(ContactFormStoreActions.ToggleForm());
	}

	setFormValidators() {
		this.validationHelper
			.control(this.bodyField, this.rulesetFields.Body)
			.control(this.canRecallField, this.rulesetFields.CanRecall)
			.control(this.emailField, this.rulesetFields.Email)
			.control(this.firstnameField, this.rulesetFields.FirstName)
			.control(this.lastnameField, this.rulesetFields.LastName)
			.control(this.phoneNumberField, this.rulesetFields.PhoneNumber)
			.control(this.subjectField, this.rulesetFields.Subject);

		this.canRecallField.valueChanges
			.pipe(takeUntil(this.destroySubscriptions$))
			.subscribe(isChecked => {
				this.recallDateField.setValue(null);
				this.recallTimeField.setValue('');

				this.recallDateField.markAsPristine();
				this.recallDateField.markAsUntouched();

				this.recallTimeField.markAsPristine();
				this.recallTimeField.markAsUntouched();

				if (isChecked) {
					this.recallTimeField.enable();
					this.recallDateField.enable();

					this.validationHelper
						.control(this.recallTimeField, this.rulesetFields.RecallTime, [Validators.required])
						.control(this.recallDateField, this.rulesetFields.RecallDate, [Validators.required]);
				} else {
					this.recallTimeField.disable();
					this.recallDateField.disable();
					ControlValidators.resetValidators(this.recallDateField);
					ControlValidators.resetValidators(this.recallTimeField);
				}
			}
			);

		this.recallDateMin = getMinDate(this.validationHelper.validationRules(this.rulesetFields.RecallDate));
	}

	getModelFromForm(): IContactForm {
		return {
			lastName: this.lastnameField.value,
			firstName: this.firstnameField.value,
			email: this.emailField.value,
			phoneNumber: this.phoneNumberField.value,
			subject: this.subjectField.value,
			body: this.bodyField.value,
			canRecall: this.canRecallField.value,
			recallDate: this.recallDateField.value,
			recallTime: (this.recallTimeField.value || '').toLowerCase()
		} as IContactForm;
	}

	onSubmit() {
		this.store.dispatch(ContactFormStoreActions.SubmitFormRequest(this.getModelFromForm()));
	}

	resetForm() {
		this.form.reset();
		this.form.patchValue(newContactForm());
	}
}
